import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { EMPTY, Subject, defer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';

import {
  BUSINESS_UNIT_OPTIONS,
  COUNTRY_OPTIONS,
  INFORMATION_CLASSIFICATION_OPTIONS,
  IT_FUNCTION_DEPARTMENT_OPTIONS,
  NEW_DEMAND_MAX_LENGTH,
  YES_NO_OPTIONS,
} from '../../../../shared/data/new-demand.constants';
import { ProjectLookup } from '../../../../shared/models/new-demand.models';
import { NewDemandService } from '../../../../shared/services/new-demand.service';

@Component({
  selector: 'app-new-demand',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, RouterLink],
  templateUrl: './new-demand.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './new-demand.component.scss',
})
export class NewDemandComponent {
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly user = inject(AuthService).user;
  private readonly newDemandService = inject(NewDemandService);
  private readonly lookupRequests = new Subject<string | null>();

  readonly maxLength = NEW_DEMAND_MAX_LENGTH;
  readonly yesNoOptions = YES_NO_OPTIONS;
  readonly businessUnits = BUSINESS_UNIT_OPTIONS;
  readonly itFunctionDepartments = IT_FUNCTION_DEPARTMENT_OPTIONS;
  readonly informationClassifications = INFORMATION_CLASSIFICATION_OPTIONS;
  readonly countries = COUNTRY_OPTIONS;

  readonly submitted = signal(false);
  readonly isDataArchival = signal(false);
  readonly isApplicationRetirement = signal(false);
  readonly lookupSucceeded = signal(false);
  readonly lookupLoading = signal(false);
  readonly lookupError = signal(false);

  readonly form = this.formBuilder.nonNullable.group({
    demandType: ['', Validators.required],
    requestorName: [
      this.user()?.displayName || this.user()?.givenName || '',
      Validators.maxLength(NEW_DEMAND_MAX_LENGTH),
    ],
    cmdbId: [
      '',
      [Validators.required, Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    ],
    applicationName: [
      '',
      [Validators.required, Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    ],
    applicationDescription: [
      '',
      [Validators.required, Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    ],
    agreedWithBusiness: [''],
    businessUnit: ['', Validators.required],
    itFunctionDepartment: ['', Validators.required],
    businessOwner: [
      '',
      [Validators.required, Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    ],
    itApplicationOwner: [
      '',
      [Validators.required, Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    ],
    sensitiveData: ['', Validators.required],
    informationClassification: ['', Validators.required],
    gxpRegulated: ['', Validators.required],
    qualityRepresentative: ['', Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
    country: ['', Validators.required],
    additionalComments: ['', Validators.maxLength(NEW_DEMAND_MAX_LENGTH)],
  });

  constructor() {
    this.form.controls.demandType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((demandType) => {
        const isDataArchival = demandType === 'data-archival';
        this.isDataArchival.set(isDataArchival);
        const isApplicationRetirement = demandType === 'application-retirement';
        this.isApplicationRetirement.set(isApplicationRetirement);
        const agreedWithBusiness = this.form.controls.agreedWithBusiness;
        if (isApplicationRetirement) {
          agreedWithBusiness.addValidators(Validators.required);
        } else {
          agreedWithBusiness.removeValidators(Validators.required);
        }
        agreedWithBusiness.updateValueAndValidity({ emitEvent: false });
        const cmdbId = this.form.controls.cmdbId;
        if (isDataArchival) {
          cmdbId.removeValidators(Validators.required);
        } else {
          cmdbId.addValidators(Validators.required);
        }
        cmdbId.updateValueAndValidity({ emitEvent: false });
      });

    this.form.controls.cmdbId.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.lookupSucceeded.set(false);
          this.lookupError.set(false);
          this.lookupRequests.next(null);
          this.clearProjectLookup();
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.lookupRequests
      .pipe(
        switchMap((cmdbId) => {
          if (cmdbId === null) {
            return EMPTY;
          }
          return defer(() => {
            this.lookupLoading.set(true);
            return this.newDemandService.getProject(cmdbId);
          }).pipe(
            tap((project) => {
              this.applyProjectLookup(project);
              this.lookupSucceeded.set(true);
              this.lookupError.set(false);
            }),
            catchError(() => {
              this.lookupError.set(true);
              return EMPTY;
            }),
            finalize(() => this.lookupLoading.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  lookupProject(): void {
    const cmdbId = this.form.controls.cmdbId.value.trim();
    this.form.controls.cmdbId.markAsTouched();
    if (!cmdbId || this.lookupLoading()) {
      return;
    }
    this.lookupSucceeded.set(false);
    this.lookupError.set(false);
    this.clearProjectLookup();
    this.lookupRequests.next(cmdbId);
  }

  private clearProjectLookup(): void {
    this.form.patchValue({
      applicationName: '',
      applicationDescription: '',
      businessUnit: '',
      itFunctionDepartment: '',
      businessOwner: '',
      itApplicationOwner: '',
      gxpRegulated: '',
      qualityRepresentative: '',
    });
  }

  private applyProjectLookup(project: ProjectLookup): void {
    this.form.patchValue({
      applicationName: project.applicationName,
      applicationDescription: project.applicationDescription,
      businessUnit: project.businessUnit,
      itFunctionDepartment: project.itFunctionDepartment,
      businessOwner: project.businessOwner,
      itApplicationOwner: project.itApplicationOwner,
      gxpRegulated: project.gxpStatus,
      qualityRepresentative: [
        project.qualityRepresentativeName,
        project.qualityRepresentativeContactDetails,
      ]
        .filter(Boolean)
        .join(' '),
    });
  }

  showError(
    controlName: keyof NewDemandComponent['form']['controls'],
  ): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.touched || this.submitted());
  }

  length(controlName: keyof NewDemandComponent['form']['controls']): number {
    return this.form.controls[controlName].value.length;
  }

  submit(): void {
    this.submitted.set(true);
    this.form.markAllAsTouched();
  }

  preventImplicitSubmit(event: Event): void {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'text'
    ) {
      event.preventDefault();
    }
  }

  async cancel(): Promise<void> {
    await this.router.navigateByUrl('/demands');
  }
}
